import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	MenuItem,
	TextField
} from '@mui/material';
import { SRAccount } from '@api/v1';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../core/actions';
import { i18n } from '../../../i18n';
import { api } from '../../api';

interface WebhookFormState {
	url: string;
	clientId: string;
	clientSecret: string;
	accessTokenUrl: string;
	scope: string;
	audience: string;
	grantType: 'client_credentials' | 'password';
	username: string;
	password: string;
}

const initialFormState: WebhookFormState = {
	url: '',
	clientId: '',
	clientSecret: '',
	accessTokenUrl: '',
	scope: '',
	audience: '',
	grantType: 'client_credentials',
	username: '',
	password: ''
};

export function AddWebhookButton({ account }: { account: SRAccount }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [formState, setFormState] = useState(initialFormState);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		setOpen(false);
		setFormState(initialFormState);
	};

	const isPasswordGrant = formState.grantType === 'password';

	const handleChange = (field: keyof WebhookFormState) => (event) => {
		setFormState((currentState) => ({
			...currentState,
			[field]: event.target.value
		}));
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		try {
			await api.post(`/api/sysadmin/accounts/${account.id}/webhook`, formState);
			dispatch(showNotification(i18n.t('sysadmin:webhook_save_success'), 'success'));
			handleClose();
		} catch {
			dispatch(showNotification(i18n.t('sysadmin:webhook_save_error'), 'error'));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
				{t('sysadmin:add_webhook')}
			</Button>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
				<DialogTitle>{t('sysadmin:add_webhook')}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						{t('sysadmin:webhook_credentials_hint')}
					</DialogContentText>
					<TextField
						autoFocus
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_url')}
						value={formState.url}
						onChange={handleChange('url')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_client_id')}
						value={formState.clientId}
						onChange={handleChange('clientId')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_client_secret')}
						value={formState.clientSecret}
						onChange={handleChange('clientSecret')}
						type="password"
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_access_token_url')}
						value={formState.accessTokenUrl}
						onChange={handleChange('accessTokenUrl')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_scope')}
						helperText={t('sysadmin:webhook_scope_hint')}
						value={formState.scope}
						onChange={handleChange('scope')}
					/>
					<TextField
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_audience')}
						helperText={t('sysadmin:webhook_audience_hint')}
						value={formState.audience}
						onChange={handleChange('audience')}
					/>
					<TextField
						select
						fullWidth
						margin="normal"
						label={t('sysadmin:webhook_grant_type')}
						helperText={t('sysadmin:webhook_grant_type_hint')}
						value={formState.grantType}
						onChange={handleChange('grantType')}
					>
						<MenuItem value="client_credentials">
							{t('sysadmin:webhook_grant_type_client_credentials')}
						</MenuItem>
						<MenuItem value="password">{t('sysadmin:webhook_grant_type_password')}</MenuItem>
					</TextField>
					{isPasswordGrant && (
						<>
							<TextField
								fullWidth
								margin="normal"
								label={t('sysadmin:webhook_username')}
								value={formState.username}
								onChange={handleChange('username')}
							/>
							<TextField
								fullWidth
								margin="normal"
								label={t('sysadmin:webhook_password')}
								value={formState.password}
								onChange={handleChange('password')}
								type="password"
							/>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary" disabled={isSubmitting}>
						{t('sysadmin:restrictions_dialog_cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						color="primary"
						disabled={
							!formState.url.trim() ||
							!formState.clientId.trim() ||
							!formState.clientSecret.trim() ||
							!formState.accessTokenUrl.trim() ||
							(isPasswordGrant &&
								(!formState.username.trim() || !formState.password.trim())) ||
							isSubmitting
						}
					>
						{t('sysadmin:restrictions_dialog_save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
